import { SIZE } from "../../shared/enums";
import { useClassNames } from "../../shared/hooks";
import dummyImage from "../../assets/images/fallback-profile-pic.png";
import style from "./index.module.scss";
import { CSSProperties, memo, useMemo } from "react";
import { BasicEmployeeInfo } from "../../model/user";
import { COLORS, COMMON_COLORS_PALETTE } from "../../shared/helpers/colors";
import { isLightColor } from "../../shared/utilities";

const { SM, MD, LG, XL } = SIZE;

type AvatarProps = {
  employeeInfo: BasicEmployeeInfo | null;
  size?: SIZE;
  customSize?: string;
  sx?: CSSProperties;
  alt?: string;
  noMargin?: boolean;
};

const Avatar = ({
  employeeInfo,
  size = SM,
  customSize,
  sx,
  alt = "profile-pic",
  noMargin,
}: AvatarProps) => {
  const classNames = useClassNames([
    style.Avatar,
    size === SM ? style["Avatar--sm"] : "",
    size === MD ? style["Avatar--md"] : "",
    size === LG ? style["Avatar--lg"] : "",
    size === XL ? style["Avatar--xl"] : "",
    noMargin ? style["Avatar--noMargin"] : "",
  ]);

  const evaluatedColor = useMemo(
    () =>
      employeeInfo
        ? COMMON_COLORS_PALETTE[
            +employeeInfo?.id
              ?.toString()
              .charAt(employeeInfo?.id?.toString().length - 1)
          ]
        : "red",
    [employeeInfo]
  );

  const employeeInitials = useMemo(
    () =>
      employeeInfo?.name
        ? employeeInfo.name
            .match(/\b(\w)/g)
            ?.slice(0, 2)
            .join("")
            .toUpperCase()
        : null,
    [employeeInfo?.name]
  );

  const textSize = useMemo(() => {
    let fontSizes = {
      caseSm: "0.625rem",
      caseMd: "0.8125rem",
      caseLg: "1.0625rem",
      caseXl: "1.5625rem",
    };

    if (employeeInitials?.length === 1) {
      fontSizes.caseSm = "0.875rem";
      fontSizes.caseMd = "1.1625rem";
      fontSizes.caseLg = "1.45625rem";
      fontSizes.caseXl = "2.1875rem";
    }

    switch (size) {
      case SM:
        return fontSizes.caseSm;
      case MD:
        return fontSizes.caseMd;
      case LG:
        return fontSizes.caseLg;
      case XL:
        return fontSizes.caseXl;
    }
  }, [employeeInitials?.length, size]);

  return employeeInfo?.profileImageUrl ? (
    <img
      className={classNames}
      style={
        customSize
          ? { width: customSize, height: customSize, ...sx }
          : { ...sx }
      }
      src={employeeInfo.profileImageUrl || dummyImage}
      alt={alt}
    />
  ) : employeeInfo?.name ? (
    <div
      className={`${classNames} ${style["Avatar-fallback"]}`}
      style={
        customSize
          ? {
              width: customSize,
              height: customSize,
              backgroundColor: evaluatedColor,
              ...sx,
            }
          : {
              backgroundColor: evaluatedColor,
              ...sx,
            }
      }
    >
      <svg width="100%" height="100%">
        <text
          fill={isLightColor(evaluatedColor) ? COLORS.grayDark : COLORS.white}
          fontSize={textSize}
          textAnchor="middle"
          x="50.5%"
          y={employeeInitials?.length === 1 ? "70%" : "64.5%"}
        >
          {employeeInitials}
        </text>
      </svg>
    </div>
  ) : null;
};

export default memo(Avatar);
